/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { expertApi } from '../../services/api';
import type { Expert, ExpertCreate } from '../../types';
import { ExpertEditForm } from './ExpertEditForm';

interface ExpertFormProps {
  open: boolean;
  onClose: () => void;
  expert?: Expert;
  onSuccess?: (expert?: Expert) => void;
  // If provided, form will hide AI System selector and use this id when creating
  aiSystemId?: number | null;
}

export const ExpertForm: React.FC<ExpertFormProps> = ({ open, onClose, expert, onSuccess, aiSystemId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const isEditing = !!expert;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [formData, setFormData] = useState<ExpertCreate>({
    name: '',
    system_prompt: '',
    prompt: '',
    model: 0,
    ai_system: null,
    input_data: {},
    output: {},
    use_cache: false,
    cache_ttl_seconds: 3600,
    cache_max_uses: null,
    retry: 3,
    expert_type: 'ANALYSIS',
    dry_run: false,
    is_active: true,
    // Evaluator defaults
    use_evaluator: false,
    evaluator_model: null,
    evaluator_system_prompt: '',
    evaluator_accuracy_threshold: 70,
    evaluator_hallucination_threshold: 30,
    evaluator_on_failure: 'continue',
    evaluator_on_low_accuracy: 'stop',
    evaluator_on_high_hallucination: 'stop',
    evaluator_max_retries: 2,
    // Execution conditions
    execution_conditions: {},
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (expert) {
      console.log('🔍 ExpertForm useEffect - expert prop:', expert);
      console.log('🔍 ExpertForm useEffect - expert.use_evaluator:', expert.use_evaluator);
      setFormData({
        name: expert.name,
        system_prompt: expert.system_prompt,
        prompt: expert.prompt,
        model: expert.model,
        ai_system: expert.ai_system,
        input_data: expert.input_data,
        output: expert.output,
        use_cache: expert.use_cache,
        cache_ttl_seconds: expert.cache_ttl_seconds ?? 3600,
        cache_max_uses: expert.cache_max_uses ?? null,
        retry: expert.retry,
        expert_type: expert.expert_type,
        dry_run: expert.dry_run,
        is_active: expert.is_active,
        code: expert.code, // Load code field for CODE experts
        // Evaluator fields
        use_evaluator: expert.use_evaluator ?? false,
        evaluator_model: expert.evaluator_model ?? null,
        evaluator_system_prompt: expert.evaluator_system_prompt ?? '',
        evaluator_accuracy_threshold: expert.evaluator_accuracy_threshold ?? 70,
        evaluator_hallucination_threshold: expert.evaluator_hallucination_threshold ?? 30,
        evaluator_on_failure: expert.evaluator_on_failure ?? 'continue',
        evaluator_on_low_accuracy: expert.evaluator_on_low_accuracy ?? 'stop',
        evaluator_on_high_hallucination: expert.evaluator_on_high_hallucination ?? 'stop',
        evaluator_max_retries: expert.evaluator_max_retries ?? 2,
        // Execution conditions
        execution_conditions: expert.execution_conditions ?? {},
      });
    } else {
      setFormData({
        name: '',
        system_prompt: '',
        prompt: '',
        model: 0,
        ai_system: null,
        input_data: { inputs: [] },
        output: {},
        use_cache: false,
        cache_ttl_seconds: 3600,
        cache_max_uses: null,
        retry: 3,
        expert_type: 'ANALYSIS',
        dry_run: false,
        is_active: true,
        // Evaluator defaults
        use_evaluator: false,
        evaluator_model: null,
        evaluator_system_prompt: '',
        evaluator_accuracy_threshold: 70,
        evaluator_hallucination_threshold: 30,
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
        evaluator_max_retries: 2,
        // Execution conditions
        execution_conditions: {},
      });
    }
    setError(null);
  }, [expert, open]);

  // If aiSystemId prop is provided and we are creating a new expert, set it
  useEffect(() => {
    if (!expert && typeof aiSystemId !== 'undefined') {
      setFormData((prev) => ({ ...prev, ai_system: aiSystemId ?? null }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expert, open, aiSystemId]);

  const createMutation = useMutation({
    mutationFn: (data: ExpertCreate) => {
      console.log('📤 CREATE MUTATION - Sending data:', data);
      console.log('📤 input_data being sent:', JSON.stringify(data.input_data, null, 2));
      // If aiSystemId prop provided, pass it as query param so backend
      // assigns the expert to that AI System and the UI can hide the field.
      if (typeof aiSystemId !== 'undefined' && aiSystemId !== null) {
        return expertApi.create(data, { ai_system: aiSystemId });
      }
      return expertApi.create(data);
    },
    onSuccess: (responseData) => {
      console.log('✅ CREATE SUCCESS - Response:', responseData);
      console.log('✅ Saved input_data:', JSON.stringify(responseData.input_data, null, 2));
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      onClose();
      onSuccess?.(responseData); // Call optional success callback with the created expert
    },
    onError: (err: any) => {
      console.error('❌ CREATE ERROR:', err);
      setError(err.response?.data?.detail || 'Failed to create expert');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ExpertCreate) => {
      console.log('📤 UPDATE MUTATION - Sending data:', data);
      console.log('📤 input_data being sent:', JSON.stringify(data.input_data, null, 2));
      return expertApi.update(expert!.id, data);
    },
    onSuccess: (responseData) => {
      console.log('✅ UPDATE SUCCESS - Response:', responseData);
      console.log('✅ Saved input_data:', JSON.stringify(responseData.input_data, null, 2));
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      onClose();
    },
    onError: (err: any) => {
      console.error('❌ UPDATE ERROR:', err);
      setError(err.response?.data?.detail || 'Failed to update expert');
    },
  });

  const handleSubmit = () => {
    // For CODE experts, prompt is not required (they use code field instead)
    if (!formData.name) {
      setError('Please fill in the expert name');
      return;
    }
    
    // Non-CODE experts require a prompt
    if (formData.expert_type !== 'CODE' && !formData.prompt) {
      setError('Please fill in the prompt');
      return;
    }

    // For AGENT type, require agent selection instead of model
    if (formData.expert_type === 'AGENT') {
      if (!formData.agent) {
        setError('Please select an agent for AGENT type expert');
        return;
      }
    } else if (formData.expert_type === 'CODE') {
      // CODE type doesn't need a model - it executes Python code directly
      if (!formData.code || !formData.code.trim()) {
        setError('Please provide Python code for CODE type expert');
        return;
      }
    } else {
      // For ANALYSIS/ACTION types, model is required
      if (!formData.model) {
        setError('Please select a model');
        return;
      }
    }

    // Prepare data for submission - ensure model is null for AGENT and CODE types
    const submitData = {
      ...formData,
      model: (formData.expert_type === 'AGENT' || formData.expert_type === 'CODE') ? null : formData.model,
      // For CODE experts, set prompt to code if prompt is empty (backend requires non-blank prompt)
      prompt: formData.expert_type === 'CODE' && !formData.prompt?.trim() 
        ? (formData.code || 'CODE_EXPERT') 
        : formData.prompt,
      // When evaluator is disabled, reset action fields to valid defaults
      ...(formData.use_evaluator ? {} : {
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
      }),
      // When CODE type, force evaluator off
      ...(formData.expert_type === 'CODE' ? {
        use_evaluator: false,
        evaluator_on_failure: 'continue',
        evaluator_on_low_accuracy: 'stop',
        evaluator_on_high_hallucination: 'stop',
      } : {}),
    };

    // DEBUG: Log the complete form data being submitted
    console.group('🔍 EXPERT FORM SUBMIT DEBUG');
    console.log('Full formData:', formData);
    console.log('submitData (with model fix):', submitData);
    console.log('input_data structure:', JSON.stringify(formData.input_data, null, 2));
    console.log('Number of inputs:', formData.input_data?.inputs?.length || 0);
    console.groupEnd();

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "lg"} 
      fullWidth 
      fullScreen={isMobile || isFullscreen}
      PaperProps={{
        sx: {
          ...((isFullscreen || isMobile) ? {} : { minHeight: '80vh', maxHeight: '90vh' })
        }
      }}
    >
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            {isEditing ? 'Edit Expert' : 'Create Expert'}
          </Typography>
          {!isMobile && (
            <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <ExpertEditForm
            expert={expert}
            formData={formData}
            onChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
            disabled={isLoading}
            hideSystemSelector={typeof aiSystemId !== 'undefined'}
            promptMinHeight={isFullscreen ? 600 : isMobile ? 350 : 450}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > button': { width: isMobile ? '100%' : 'auto' }
      }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
